import { ApolloLink } from "@apollo/client";
import {
  AppState,
  AppStateStatus,
  NativeEventSubscription,
} from "react-native";
import { logger } from "../../Utils/logger";

/**
 * DHT-5532: a request that never reached the server (React Native's fetch
 * rejecting with `TypeError: Network request failed`, no HTTP status) is a
 * device losing its connection, not an application fault. Both GraphQL
 * clients report it as a RUM action instead of an error, and this module
 * counts those failures so a sustained outage still raises exactly one error.
 *
 * DHT-5616: web is included too. Browsers reject a dropped fetch with their
 * own wording (Chrome/Edge, Safari, Firefox), matched exactly below.
 *
 * Hidden tabs need no listener of their own: react-native-web's AppState is
 * built on `document.visibilityState` (hidden reads as "background") and
 * fires "change" on `visibilitychange`, so the AppState check and reset below
 * cover a hidden tab exactly as they cover a backgrounded app.
 *
 * Offline is web-only: a failure while `navigator.onLine === false` is not
 * counted, and the window "offline" event resets the count. React Native's
 * navigator has no `onLine` and its window no `addEventListener`, so the
 * `typeof` guards keep both inert there (one shared file rather than a
 * `.web.ts` split, because the count has to span both clients on every
 * platform).
 *
 * Module state rather than a hook: both error links are rebuilt inside their
 * hooks (the Autumn one on every render), and the count has to span both
 * clients.
 */

/** Consecutive transport failures before the escalation can fire. */
export const SUSTAINED_FAILURE_COUNT = 10;
/** Minimum span, first failure to latest, before the escalation can fire. */
export const SUSTAINED_FAILURE_MS = 120_000;

const TRANSPORT_FAILURE_MESSAGES: ReadonlySet<string> = new Set([
  // React Native
  "Network request failed",
  // Chrome, Edge
  "Failed to fetch",
  // Safari
  "Load failed",
  // Firefox
  "NetworkError when attempting to fetch resource.",
]);

let count = 0;
let firstAt: number | null = null;
let lastOperation: string | null = null;
let fired = false;
let appStateSubscription: NativeEventSubscription | null = null;
let offlineListenerRegistered = false;

/**
 * Offline Access (ClinicApp/features/OfflineAccess): a second, faster signal
 * alongside the escalation above. The escalation waits for ten failures over
 * two minutes before raising one error; a person at the front desk needs to
 * know far sooner that changes will not save, so three consecutive transport
 * failures (or the browser's own "offline" event) mark the connection lost,
 * and the first result to arrive marks it restored. Listeners are told only
 * when that state changes. Nothing here changes the escalation's behaviour.
 */
export const CONNECTION_LOST_COUNT = 3;

type ConnectionListener = (lost: boolean) => void;

const connectionListeners = new Set<ConnectionListener>();
let connectionLost = false;
let consecutiveFailures = 0;

const setConnectionLost = (lost: boolean) => {
  if (lost === connectionLost) return;
  connectionLost = lost;
  for (const listener of connectionListeners) listener(lost);
};

/** Whether the connection currently reads as lost. */
export const isConnectionLost = () => connectionLost;

/**
 * Subscribes to the connection-lost signal. The listener is called only when
 * the state changes; read {@link isConnectionLost} for the current value.
 * @returns A function that unsubscribes.
 */
export const subscribeConnectionLost = (listener: ConnectionListener) => {
  connectionListeners.add(listener);
  return () => {
    connectionListeners.delete(listener);
  };
};

/**
 * Exact match on name AND message, with no status code. Any other TypeError
 * (a timeout, a message with extra text), a non-TypeError, or anything
 * carrying a status does not match, so it keeps logging at error.
 */
export const isTransportFailure = (
  error: { name?: string; message?: string } | null | undefined,
  statusCode: unknown,
): boolean =>
  !!error &&
  error.name === "TypeError" &&
  error.message != null &&
  TRANSPORT_FAILURE_MESSAGES.has(error.message) &&
  statusCode == null;

const reset = () => {
  count = 0;
  firstAt = null;
  lastOperation = null;
  fired = false;
};

// Any state other than "active" resets, iOS "inactive" included (a permission
// prompt, control centre). That can re-arm a sustained outage early, which
// errs toward one more escalation rather than a missed one; accepted (plan P5).
const onAppStateChange = (next: AppStateStatus) => {
  if (next !== "active") reset();
};

const onOffline = () => {
  reset();
  setConnectionLost(true);
};

// The browser has a network again: drop the suspicion an offline event left.
// An idle screen may make no request to clear it, and a stale suspicion would
// stop a later outage from starting a fresh check (useConfirmedOffline only
// probes when the suspicion begins). Real failures re-raise it.
const onOnline = () => {
  consecutiveFailures = 0;
  setConnectionLost(false);
};

const canListenOnWindow = () =>
  typeof window !== "undefined" &&
  typeof window.addEventListener === "function";

const isBrowserOffline = () =>
  typeof navigator !== "undefined" && navigator.onLine === false;

// Registered lazily on the first failure, never at import: no failure arrives
// while backgrounded or offline, so those transitions have to be observed
// directly, but unrelated bundles and tests should not carry a listener.
const ensureSubscriptions = () => {
  if (!appStateSubscription) {
    appStateSubscription = AppState.addEventListener(
      "change",
      onAppStateChange,
    );
  }
  if (!offlineListenerRegistered && canListenOnWindow()) {
    window.addEventListener("offline", onOffline);
    window.addEventListener("online", onOnline);
    offlineListenerRegistered = true;
  }
};

export const recordTransportFailure = (operation: string) => {
  ensureSubscriptions();

  // Only failures while the app is in the foreground (on web: the tab is
  // visible) and the browser believes it is online count.
  if (AppState.currentState !== "active") return;

  consecutiveFailures += 1;
  if (consecutiveFailures >= CONNECTION_LOST_COUNT || isBrowserOffline()) {
    setConnectionLost(true);
  }

  if (isBrowserOffline()) return;

  const now = Date.now();
  count += 1;
  if (firstAt == null) firstAt = now;
  lastOperation = operation;

  const sinceMs = now - firstAt;
  if (
    !fired &&
    count >= SUSTAINED_FAILURE_COUNT &&
    sinceMs >= SUSTAINED_FAILURE_MS
  ) {
    fired = true;
    logger.error("Sustained network failure", {
      count,
      sinceMs,
      lastOperation,
    });
  }
};

/** Any result reaching the client proves the transport is up: re-arms. */
export const recordTransportSuccess = () => {
  reset();
  consecutiveFailures = 0;
  setConnectionLost(false);
};

/**
 * A pass-through link that resets the counter on every result, including one
 * carrying GraphQL errors and subscription events. It never errors or drops an
 * operation, so it is not the offline link DHT-4637 removed.
 */
export const createTransportSuccessLink = () =>
  new ApolloLink((operation, forward) =>
    forward(operation).map(result => {
      recordTransportSuccess();
      return result;
    }),
  );

/** Test seam: clears the counter and removes the AppState, offline and online listeners. */
export const __resetNetworkFailureTrackerForTests = () => {
  reset();
  consecutiveFailures = 0;
  connectionLost = false;
  connectionListeners.clear();
  appStateSubscription?.remove();
  appStateSubscription = null;
  if (offlineListenerRegistered && canListenOnWindow()) {
    window.removeEventListener("offline", onOffline);
    window.removeEventListener("online", onOnline);
  }
  offlineListenerRegistered = false;
};
